<template>
  <div class="social-profile" :style="styles" :class="classes">
    <RatioBox :ratio="350 / 400">
      <div class="social-profile__inner">
        <template v-if="variant === 'qr'">
          <div class="social-profile__qr">
            <slot name="icon" />
          </div>
          <div class="social-profile__link-wrapper">
            <InlineLink
              class="social-profile__link"
              @click="$emit('click')"
              color="black"
            >
              <slot name="link">campsite.bio/acmestudios</slot>
            </InlineLink>
          </div>
        </template>
        <template v-else-if="variant === 'email'">
          <div class="social-profile__top">
            <div class="social-profile__image">
              <Shape
                width="60px"
                height="60px"
                circle
                block
                :color="shapeColor"
              >
                <slot name="icon" />
              </Shape>
            </div>
            <div class="social-profile__top__copy">
              <Shape width="100%" height="26px" :color="shapeColor" />
              <Shape width="75%" height="16px" :color="shapeColor" />
            </div>
          </div>
          <div class="social-profile__link-wrapper">
            <Shape width="100%" height="22px" :color="shapeColor" />
            <Shape width="100%" height="22px" :color="shapeColor" />
            <Shape width="100%" height="22px" :color="shapeColor" />
            <InlineLink
              class="social-profile__link"
              @click="$emit('click')"
              color="black"
            >
              <slot name="link">campsite.bio/acmestudios</slot>
            </InlineLink>
          </div>
        </template>
        <template v-else>
          <div class="social-profile__top">
            <div class="social-profile__image">
              <Shape
                width="60px"
                height="60px"
                circle
                block
                :color="shapeColor"
              >
                <slot name="icon" />
              </Shape>
            </div>
            <div class="social-profile__top__copy">
              <Shape width="100%" height="26px" :color="shapeColor" />
              <Shape width="75%" height="16px" :color="shapeColor" />
            </div>
          </div>
          <div>
            <InlineLink
              class="social-profile__link"
              @click="$emit('click')"
              color="black"
            >
              <slot name="link">campsite.bio/acmestudios</slot>
            </InlineLink>
          </div>
          <div class="social-profile__grid-wrapper">
            <div class="social-profile__grid">
              <Shape
                v-for="i in 6"
                :key="i"
                width="100%"
                block
                :color="shapeColor"
              />
            </div>
          </div>
        </template>
      </div>
    </RatioBox>
  </div>
</template>

<script>
import { Shape, InlineLink, RatioBox } from '@campsite-bio/component-lib';
import { computed, toRefs } from '@nuxtjs/composition-api';
import tinycolor from 'tinycolor2';

export default {
  components: {
    Shape,
    InlineLink,
    RatioBox,
  },

  props: {
    color: {
      type: String,
      default: '#000',
    },

    variant: {
      type: String,
      default: null,
    },
  },

  setup(props) {
    const { color, variant } = toRefs(props);

    const shapeColor = computed(() => {
      const currentColor = tinycolor(color.value);
      return currentColor.isDark()
        ? currentColor.lighten(10).toRgbString()
        : currentColor.darken(10).toRgbString();
    });

    const styles = computed(() => {
      return {
        '--c-profile-color': color.value,
      };
    });

    const classes = computed(() => {
      return {
        [`social-profile--${variant.value}`]: variant.value,
      };
    });

    return { styles, classes, shapeColor };
  },
};
</script>

<style lang="scss" scoped>
.social-profile {
  background: var(--c-profile-color);
  border-radius: var(--g-border-radius-standard);
  border-radius: Min(
    var(--g-border-radius-standard),
    var(--g-border-radius-min)
  );
  box-shadow: 1px 1px 3px 1px rgba(0, 0, 0, 0.15);
  color: #fff;
  text-align: left;

  &__inner {
    padding: 0.75rem 0.75rem 0;
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  &__top {
    align-items: center;
    display: flex;
    margin-bottom: 0.75rem;

    &__copy {
      margin-left: 0.75rem;
      flex-grow: 1;
    }
  }

  &__image {
    position: relative;

    &::v-deep {
      .v-shape {
        align-items: center;
        display: flex;
        justify-content: center;
        font-size: 0;
      }

      .v-svg-icon {
        color: var(--c-profile-color);
        font-size: 2.5rem;
        transform: scale(1.01) translateZ(0);
      }
    }
  }

  &__grid-wrapper {
    position: relative;
    overflow: hidden;
    flex-grow: 1;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: 1fr 1fr;
    grid-gap: 2px;
    margin-top: 0.75rem;
    position: absolute;
    width: 100%;

    &::v-deep {
      .v-shape {
        border-radius: 0px;
        padding-bottom: 100%;
        display: block;
        height: 0;
      }
    }
  }

  // QR

  &--qr {
    .social-profile__link-wrapper {
      position: absolute;
      bottom: 0.75rem;
      left: 0.75rem;
      width: calc(100% - 1.5rem);
      text-align: center;
    }
  }

  &__qr {
    justify-content: center;
    display: flex;

    &::v-deep {
      .v-svg-icon {
        margin-top: -9%;
        height: auto;
        width: 90%;
      }
    }
  }

  // Email

  &--email {
    .social-profile__top {
      margin-bottom: 0.25rem;
    }

    .social-profile__link {
      margin-top: 0.5rem;
    }
  }
}

.social-profile__link.social-profile__link {
  box-shadow: rgba(0, 0, 0, 0.11) 0.2px 0.4px 0.5px 0px,
    rgba(0, 0, 0, 0.11) 0.8px 1.4px 1.8px -0.8px,
    rgba(0, 0, 0, 0.11) 1.9px 3.4px 4.4px -1.7px,
    rgba(0, 0, 0, 0.11) 4.7px 8.2px 10.6px -2.5px;
  border-radius: var(--g-border-radius-standard);
  border-radius: Min(
    var(--g-border-radius-standard),
    var(--g-border-radius-min)
  );
  background: #fff;
  font-size: 0.75rem;
  padding: 0.35em 0.35em 0.45em 0.65em;
  text-decoration: none;
  max-width: 100%;

  &::v-deep {
    .v-inline-link__content {
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &:hover,
  &:focus {
    background: #fff;
    color: #222;
  }
}
</style>
